import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Empty, Layout, Popconfirm, Space, Spin, Table, Tag, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { getApiErrorMessage, serverFetch } from '@api/server';
import { useAuth } from '@context/auth-context';

const { Content } = Layout;
const { Text, Title } = Typography;

export const getUsers = () => serverFetch('user');

export const getUser = (id) => serverFetch(`user/${id}`);

export const createUser = (data) => serverFetch('user', {
  method: 'POST',
  bodyData: data,
});

export const updateUser = (data) => serverFetch('user', {
  method: 'PUT',
  bodyData: data,
});

export const deleteUser = (id) => serverFetch(`user/${id}`, {
  method: 'DELETE',
});

const Users = () => {
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const [users, setUsers] = useState([]);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [deleteConfirmUserId, setDeleteConfirmUserId] = useState(null);

  const loadUsers = useCallback(async () => {
    setError('');
    setIsLoading(true);
    try {
      const result = await getUsers();
      setUsers(result || []);
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const handleDeleteUser = async (userId) => {
    setError('');
    setDeletingUserId(userId);
    try {
      await deleteUser(userId);
      if (currentUser?.id === userId) {
        await logout().catch(() => {
          // The backend already removed this user's sessions.
        });
        return;
      }
      setUsers((currentUsers) => currentUsers.filter((user) => user.id !== userId));
    } catch (requestError) {
      setError(getApiErrorMessage(requestError));
    } finally {
      setDeletingUserId(null);
      setDeleteConfirmUserId(null);
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 90,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      render: (email) => <Text strong>{email}</Text>,
    },
    {
      title: 'Имя',
      dataIndex: 'name',
    },
    {
      title: 'Роль',
      dataIndex: 'admin',
      width: 140,
      render: (admin) => (
        <Tag color={admin ? 'blue' : 'default'}>
          {admin ? 'Администратор' : 'Пользователь'}
        </Tag>
      ),
    },
    {
      title: 'Статус',
      dataIndex: 'active',
      width: 120,
      render: (active) => (
        <Tag color={active ? 'green' : 'red'}>
          {active ? 'Активен' : 'Отключен'}
        </Tag>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 80,
      render: (_, user) => (
        <span
          className={
            deleteConfirmUserId === user.id || deletingUserId === user.id
              ? 'table-row-action table-row-action-visible'
              : 'table-row-action'
          }
          onClick={(event) => event.stopPropagation()}
        >
          <Popconfirm
            title="Удалить пользователя?"
            description="Будут удалены все сессии пользователя."
            okText="Удалить"
            cancelText="Отмена"
            open={deleteConfirmUserId === user.id}
            okButtonProps={{ danger: true, loading: deletingUserId === user.id }}
            onOpenChange={(open) => setDeleteConfirmUserId(open ? user.id : null)}
            onConfirm={() => handleDeleteUser(user.id)}
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              aria-label="Удалить"
              loading={deletingUserId === user.id}
            />
          </Popconfirm>
        </span>
      ),
    },
  ];

  return (
    <Content className="users-page">
      <div className="users-panel">
        <div className="panel-title users-title">
          <Space direction="vertical" size={0}>
            <Title level={5}>Пользователи</Title>
            <Text type="secondary">{users.length}</Text>
          </Space>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={loadUsers}>
              Обновить
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/users/new')}>
              Создать
            </Button>
          </Space>
        </div>

        {error && (
          <Alert className="panel-alert" type="error" message={error} showIcon />
        )}

        <Spin spinning={isLoading}>
          <Table
            rowKey="id"
            columns={columns}
            dataSource={users}
            locale={{ emptyText: <Empty description="Пользователей пока нет" /> }}
            pagination={false}
            onRow={(user) => ({
              onClick: () => navigate(`/users/${user.id}`),
            })}
          />
        </Spin>
      </div>
    </Content>
  );
};

export default Users;
